import dayjs from 'dayjs';
import { formatLocalDate } from './format';
import {
  REG_FORM_SUBMISSION_INVOICE_STATUS_LIST,
  WEBSITE_PAYMENT_STATUS_ENUM,
  WEBSITE_PAYMENT_STATUS_LIST
} from '../config/constants';

const PROCESSING_COLOR = 'orange';

const getInstallmentStatusInfo = (fkWebsitePaymentStatus) =>
  WEBSITE_PAYMENT_STATUS_LIST.find(
    (s) => s.value === fkWebsitePaymentStatus?.toString()
  ) ?? { label: 'Unknown', color: '#000', value: null };

const getInvoiceStatusInfo = (status) =>
  REG_FORM_SUBMISSION_INVOICE_STATUS_LIST.find((s) => s.value === status) ?? {
    label: 'Unknown',
    color: 'gray',
    value: null
  };

const getPaymentPlanSummary = (plan) => {
  const installments = plan?.installments ?? [];
  const needsCardUpdate = !!plan?.hasFailedInstallment;
  const isProcessing = !needsCardUpdate && !!plan?.hasPastDueInstallment;
  const isFullyPaid =
    installments.length > 0 &&
    installments.every(
      (i) =>
        i.fkWebsitePaymentStatus?.toString() ===
        WEBSITE_PAYMENT_STATUS_ENUM.PAID
    );
  const isCompleted =
    installments.length > 0 &&
    installments.every(
      (i) =>
        i.fkWebsitePaymentStatus?.toString() ===
          WEBSITE_PAYMENT_STATUS_ENUM.PAID ||
        i.fkWebsitePaymentStatus?.toString() ===
          WEBSITE_PAYMENT_STATUS_ENUM.CANCELLED ||
        i.fkWebsitePaymentStatus?.toString() ===
          WEBSITE_PAYMENT_STATUS_ENUM.REFUNDED
    );

  const nextDueLabel = plan?.nextDueDate
    ? dayjs(formatLocalDate(plan.nextDueDate)).format('MMM D, YYYY')
    : null;

  const paidStatus = getInstallmentStatusInfo(WEBSITE_PAYMENT_STATUS_ENUM.PAID);
  const failedStatus = getInstallmentStatusInfo(
    WEBSITE_PAYMENT_STATUS_ENUM.FAILED
  );

  return {
    statusLabel: isFullyPaid
      ? 'Fully paid'
      : isCompleted
      ? 'Completed'
      : needsCardUpdate
      ? 'Payment failed'
      : isProcessing
      ? 'Processing'
      : nextDueLabel
      ? 'Scheduled'
      : 'No payments due',
    statusColor: isFullyPaid
      ? paidStatus.color
      : isCompleted
      ? 'gray'
      : needsCardUpdate
      ? failedStatus.color
      : isProcessing
      ? PROCESSING_COLOR
      : 'blue',
    nextDueLabel,
    outstandingInCents:
      (plan?.scheduledInCents ?? 0) + (plan?.failedInCents ?? 0),
    isCompleted,
    isFullyPaid,
    isProcessing,
    needsCardUpdate
  };
};

const getInvoiceSummary = (invoice) => {
  const status = getInvoiceStatusInfo(invoice?.status);
  const amountInCents = invoice?.amountInCents ?? 0;
  const balanceInCents = invoice?.balanceInCents ?? 0;
  const amountRefundedInCents = invoice?.amountRefundedInCents ?? 0;

  return {
    statusLabel: status.label,
    statusColor: status.color,
    isPayable: !!invoice?.isPayable,
    isPartiallySettled:
      !!invoice?.isOutstanding && balanceInCents !== amountInCents,
    hasRefund: amountRefundedInCents > 0
  };
};

const getCustomerPaymentAlerts = (customerPayments) => {
  const paymentPlans = customerPayments?.paymentPlans ?? [];
  const invoices = customerPayments?.invoices ?? [];

  const failedPaymentPlans = paymentPlans.filter((p) => p.hasFailedInstallment);
  const processingPaymentPlans = paymentPlans.filter(
    (p) => !p.hasFailedInstallment && p.hasPastDueInstallment
  );
  const outstandingInvoices = invoices.filter((i) => i.isOutstanding);

  return {
    failedPaymentPlans,
    processingPaymentPlans,
    outstandingInvoices,
    hasFailedPaymentPlan: failedPaymentPlans.length > 0,
    hasProcessingPaymentPlan: processingPaymentPlans.length > 0,
    hasOutstandingInvoice: outstandingInvoices.length > 0
  };
};

export {
  getCustomerPaymentAlerts,
  getInstallmentStatusInfo,
  getInvoiceStatusInfo,
  getInvoiceSummary,
  getPaymentPlanSummary,
  PROCESSING_COLOR
};
